import { useState } from 'react';
import './Remove.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://goodfetch-api.workers.dev';

function Remove() {
  const [url, setUrl] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removalId, setRemovalId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url, email, reason }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Removal request failed');
      }

      setRemovalId(data.removal_id);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="remove-page">
        <div className="container">
          <div className="remove-success card">
            <div className="success-icon">&#10003;</div>
            <h2>Removal Request Submitted</h2>
            <p className="text-secondary">
              The content at <strong>{url}</strong> has been removed from search results.
            </p>
            
            {removalId && (
              <div className="removal-id mt-4">
                <p className="text-secondary">Your removal ID:</p>
                <code>{removalId}</code>
              </div>
            )}

            <p className="text-secondary mt-4">
              To complete permanent removal, please verify ownership using one of the
              methods sent to your email.
            </p>

            <div className="mt-8">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setSubmitted(false);
                  setUrl('');
                  setEmail('');
                  setReason('');
                  setRemovalId(null);
                }}
              >
                Submit Another Request
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="remove-page">
      <div className="container">
        <div className="remove-header">
          <h1>Request Content Removal</h1>
          <p className="text-secondary">
            Content owners can request removal of their content from the GoodFetch index.
          </p>
        </div>

        <div className="remove-content">
          <div className="remove-form-section card">
            <h3>Removal Request</h3>
            
            {error && (
              <div className="error-message">
                <p className="text-error">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="remove-form">
              <div className="form-group">
                <label htmlFor="url">URL to Remove</label>
                <input
                  type="url"
                  id="url"
                  className="input"
                  placeholder="https://example.com/page"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                />
                <p className="form-hint text-secondary">
                  Enter the full URL of the content you want removed
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="email">Your Email</label>
                <input
                  type="email"
                  id="email"
                  className="input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className="form-hint text-secondary">
                  For ownership verification
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="reason">Reason (optional)</label>
                <textarea
                  id="reason"
                  className="input"
                  placeholder="Why are you requesting removal?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Request Removal'}
              </button>
            </form>
          </div>

          <div className="remove-info card">
            <h3>How Removal Works</h3>
            <div className="info-steps">
              <div className="info-step">
                <div className="step-number">1</div>
                <div>
                  <h4>Immediate Soft Delete</h4>
                  <p className="text-secondary">
                    Content is immediately removed from search results and API responses.
                  </p>
                </div>
              </div>
              <div className="info-step">
                <div className="step-number">2</div>
                <div>
                  <h4>Ownership Verification</h4>
                  <p className="text-secondary">
                    Verify ownership via email, DNS TXT record, or HTML meta tag.
                  </p>
                </div>
              </div>
              <div className="info-step">
                <div className="step-number">3</div>
                <div>
                  <h4>Permanent Removal</h4>
                  <p className="text-secondary">
                    After verification, content is permanently deleted from our systems.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Remove;
